<template>
  <div v-if="view" class="lin-block" :class="'lin-block-' + view.kind">
    <!-- 整個家族（作品頁用，或家族夠小時草圖頁也直接整棵展開） -->
    <template v-if="view.kind === 'tree'">
      <p class="lin-block-lead">{{ lead }}</p>
      <lineage-tree :trees="view.family.trees" :zh="zh" :from="from" :current="subject === 'work' ? undefined : sketchId" :horizontal="mode === 'row'" />
      <p class="lin-block-more"><router-link :to="familyPath">{{ zh ? `在族譜裡看這個家族 →` : `See this family on the tree →` }}</router-link></p>
    </template>

    <!-- 部分展開：源頭 → … → 母體 → 這件 → 衍生（草圖頁用，家族大時） -->
    <template v-else-if="view.kind === 'strip'">
      <p class="lin-block-lead">{{ lead }}</p>
      <ol class="lin-strip" :aria-label="zh ? '血緣' : 'Lineage'">
        <li v-if="view.origin" class="lin-strip-item"><span class="lin-strip-role">{{ zh ? '源頭' : 'origin' }}</span><lineage-card :n="view.origin" :zh="zh" :from="from" small /></li>
        <li v-if="view.skipped" class="lin-strip-gap" aria-hidden="true"><span>… {{ zh ? `${view.skipped} 代` : `${view.skipped} gen.` }}</span></li>
        <li v-if="view.parent" class="lin-strip-item"><span class="lin-strip-role">{{ zh ? '母體' : 'parent' }}<em v-if="view.node.k === 'sim'"> · {{ zh ? '推論' : 'inferred' }}</em></span><lineage-card :n="view.parent" :zh="zh" :from="from" small /></li>
        <li class="lin-strip-item lin-strip-current"><span class="lin-strip-role">{{ zh ? '這件' : 'this one' }}</span><lineage-card :n="view.node" :zh="zh" :from="from" small hl /></li>
        <li v-for="c in view.children" :key="c.id" class="lin-strip-item"><span class="lin-strip-role">{{ zh ? '衍生' : 'child' }}<em v-if="c.k === 'sim'"> · {{ zh ? '推論' : 'inferred' }}</em></span><lineage-card :n="c" :zh="zh" :from="from" small /></li>
        <li v-if="view.moreChildren" class="lin-strip-gap"><router-link :to="familyPath">+{{ view.moreChildren }}</router-link></li>
      </ol>
      <p v-if="view.siblings.length" class="lin-block-siblings">
        <span>{{ zh ? '同一代' : 'Same generation' }}：</span>
        <template v-for="(s, i) in view.siblings" :key="s.id"><router-link v-if="!s.p" :to="sketchPath(s)">{{ label(s) }}</router-link><span v-else>{{ label(s) }}</span><template v-if="i < view.siblings.length - 1"> · </template></template>
      </p>
      <p class="lin-block-more"><router-link :to="familyPath">{{ zh ? `整個家族 ${view.family.n} 件，在族譜裡看 →` : `The whole family (${view.family.n}) on the tree →` }}</router-link></p>
    </template>

    <!-- 從模板起步（不算血緣，但值得寫一句） -->
    <p v-else-if="view.kind === 'template'" class="lin-block-lead">
      {{ zh ? '這件從共用的起始模板開始畫：' : 'This one began from a shared starter template: ' }}
      <router-link v-if="!view.template.p" :to="sketchPath(view.template)">{{ view.template.t }}</router-link><span v-else>{{ view.template.t }}</span>
      {{ zh ? `。從同一張模板起步的還有 ${view.template.started - 1} 件，但那不算血緣。` : `. ${view.template.started - 1} others started from the same template, which is not kinship.` }}
      <router-link :to="localPath('/lineage#templates')">{{ zh ? '看起始模板 →' : 'See the templates →' }}</router-link>
    </p>

    <!-- 在實驗室裡直接開的草圖（2026-10-05）：沒有 OpenProcessing 編號，官網的族譜樹裡沒有它，只畫一張小卡連回實驗室。
         縮圖是 npm run lab:sync 存進 repo 的檔，未公開的照實驗室的規矩只寫名字、不給連結。 -->
    <template v-else-if="view.kind === 'lab'">
      <p class="lin-block-lead">{{ labLead }}</p>
      <a v-if="view.work.public && view.work.url" class="lin-lab-card" :href="view.work.url" target="_blank" rel="noopener">
        <img v-if="view.work.thumb" :src="view.work.thumb" :srcset="view.work.thumb2x ? `${view.work.thumb} 1x, ${view.work.thumb2x} 2x` : undefined" :alt="labTitle" width="240" height="240" loading="lazy" decoding="async" />
        <span class="lin-lab-text">
          <span class="lin-lab-title">{{ labTitle }}</span>
          <span v-if="view.work.date" class="lin-lab-date">{{ view.work.date }}</span>
          <span class="lin-lab-go">{{ zh ? '在實驗室看這件 ↗︎' : 'See it in the Lab ↗︎' }}</span>
        </span>
      </a>
      <p v-else class="lin-lab-private"><span class="lin-lab-title">{{ labTitle }}</span><span class="lin-lab-date">{{ zh ? '未公開' : 'private' }}</span></p>
    </template>
  </div>
</template>

<script setup>
import { withSlash } from '@/runtime/site-path.mjs'
import { computed } from 'vue'
import data from '@/data/lineage.json'
import labEmbeds from '@/data/lab-embeds.json'
import labWorks from '@/data/lab-works.json'
import { makeLineageResolver } from './lineage-ref.mjs'
import LineageTree from './LineageTree.vue'
import LineageCard from './LineageCard.vue'

const props = defineProps({
  sketch: { type: String, required: true },   // OpenProcessing visualID，或實驗室的 slug（lineage-ref.mjs：搬進實驗室的換回 visualID，實驗室出生的畫小卡）
  mode: { type: String, default: 'strip' },   // 'strip'（草圖頁：部分展開）、'tree'（作品頁：整個家族，由上往下）或 'row'（整個家族，由左往右；2026-09-20）
  zh: Boolean,
  from: String,
  subject: { type: String, default: 'sketch' },   // 'sketch'：這一頁就是那件草圖；'work'：這一頁是展覽作品，樹是它長出來的家族
})
const INLINE_TREE_MAX = 8   // 家族不超過這麼多件時，草圖頁也直接整棵展開

// 查這件在哪個家族、誰是父、誰是子。一次走完所有樹，做成 id → 節點／父 的表。
const index = (() => {
  const byId = {}, parentOf = {}, familyOf = {}, depthOf = {}
  const walk = (n, fam, p, depth) => {
    byId[n.id] = n; familyOf[n.id] = fam; depthOf[n.id] = depth
    if (p) parentOf[n.id] = p
    for (const c of n.c || []) walk(c, fam, n, depth + 1)
  }
  for (const f of data.families) for (const t of f.trees) walk(t, f, null, 0)
  return { byId, parentOf, familyOf, depthOf }
})()
// 孤點（不在任何家族）也要查得到：它可能是從模板起步的
const isolatedById = Object.fromEntries(data.isolated.flatMap(y => y.items.map(n => [n.id, n])))
const templateById = Object.fromEntries(data.templates.map(t => [t.id, t]))

// 寫的是 slug 也行：從 OpenProcessing 搬進實驗室的換回 visualID（之後跟寫 visualID 一模一樣），實驗室出生的走下面的小卡
const resolveRef = makeLineageResolver({ embeds: labEmbeds.items, labWorks: labWorks.items })
const target = computed(() => resolveRef(props.sketch))
const sketchId = computed(() => (target.value.kind === 'op' ? target.value.id : ''))

const view = computed(() => {
  if (target.value.kind === 'lab') return { kind: 'lab', work: target.value.work }
  const node = index.byId[sketchId.value]
  if (!node) {
    const lone = isolatedById[sketchId.value]
    if (lone?.tpl && templateById[lone.tpl]) return { kind: 'template', template: templateById[lone.tpl] }
    return null   // 模板自己、或沒有任何血緣的孤點：不畫
  }
  if (node.tpl && templateById[node.tpl] && !index.parentOf[sketchId.value]) {
    // 在家族裡當根、而且是從模板起步的：整棵樹照畫，開頭多一句模板
  }
  const family = index.familyOf[sketchId.value]
  if (props.mode === 'tree' || props.mode === 'row' || family.n <= INLINE_TREE_MAX) return { kind: 'tree', family, node }
  // 部分展開
  const chain = []
  let cur = index.parentOf[sketchId.value]
  while (cur) { chain.unshift(cur); cur = index.parentOf[cur.id] }
  const parent = chain[chain.length - 1] || null
  const origin = chain.length > 1 ? chain[0] : null
  const skipped = Math.max(0, chain.length - 2)
  const children = (node.c || []).slice(0, 6)
  const moreChildren = Math.max(0, (node.c || []).length - children.length)
  const siblings = parent ? (parent.c || []).filter(s => s.id !== node.id).slice(0, 6) : []
  return { kind: 'strip', family, node, origin, skipped, parent, children, moreChildren, siblings }
})

const lead = computed(() => {
  const v = view.value
  if (!v || !v.family) return ''
  const f = v.family
  const name = props.zh && f.nameZh ? f.nameZh : f.name
  if (props.subject === 'work') {
    if (props.zh) return `這件作品從「${name}」家族長出來：${f.n} 件每日創作，${f.span[0]} 到 ${f.span[1]}。${f.forks ? `其中 ${f.forks} 條血緣是他按過 fork` : ''}${f.sims ? `${f.forks ? '，' : ''}${f.sims} 條是從程式碼推論的` : ''}${f.priv ? `；${f.priv} 件未公開，看得到、點不開` : ''}。`
    return `This work grew out of the “${name}” family: ${f.n} daily sketches, ${f.span[0]} to ${f.span[1]}. ${f.forks ? `${f.forks} of the links are forks` : ''}${f.sims ? `${f.forks ? ', ' : ''}${f.sims} inferred from the code` : ''}${f.priv ? `; ${f.priv} are private, shown but not linked` : ''}.`
  }
  if (props.zh) return `這件屬於「${name}」家族：${f.n} 件，${f.span[0]} 到 ${f.span[1]}。${f.forks ? `其中 ${f.forks} 條血緣是他按過 fork` : ''}${f.sims ? `${f.forks ? '，' : ''}${f.sims} 條是從程式碼推論的` : ''}。`
  return `Part of the “${name}” family: ${f.n} works, ${f.span[0]} to ${f.span[1]}. ${f.forks ? `${f.forks} of the links are forks` : ''}${f.sims ? `${f.forks ? ', ' : ''}${f.sims} inferred from the code` : ''}.`
})
// 實驗室出生的那件：標題照實驗室（中文頁先用中文名），說明只講看得到的事實：它沒有 OpenProcessing 編號，所以這裡沒有族譜樹
const labTitle = computed(() => {
  const w = view.value?.kind === 'lab' ? view.value.work : null
  if (!w) return ''
  return (props.zh && w.titleZh) || w.title || w.slug
})
const labLead = computed(() => {
  const w = view.value?.kind === 'lab' ? view.value.work : null
  if (!w) return ''
  if (!w.public || !w.url) {
    return props.zh
      ? '這件作品從實驗室的一件草圖長出來。那件沒有公開，這裡只寫名字。'
      : 'This work grew out of a sketch in the Lab that is not public, so only its name is given here.'
  }
  return props.zh
    ? '這件作品從實驗室的一件草圖長出來。那件是在實驗室裡直接開的，沒有 OpenProcessing 編號，所以這裡沒有它的族譜樹。程式、版本與族譜都在實驗室。'
    : 'This work grew out of a sketch that was started in the Lab itself. It has no OpenProcessing number, so there is no family tree for it here; its code, versions and lineage live in the Lab.'
})
const localPath = p => withSlash((props.zh ? '/zh' : '') + p)
const familyPath = computed(() => localPath('/lineage#' + (view.value?.family?.id || '')))
const sketchPath = n => localPath('/sketch/' + n.id) + (props.from ? '?from=' + encodeURIComponent(props.from) : '')
const label = n => (props.zh && n.tz) ? n.tz : n.t
</script>

<style>
.lin-block{display:grid;gap:14px}
.lin-block-lead{font-size:.95rem;line-height:1.7;color:var(--ink-2,#b4b4b0);margin:0}
.lin-block-more{margin:0;font-size:.9rem}
.lin-block-more a{color:var(--gold,#d9c267)}
.lin-strip{list-style:none;margin:0;padding:6px 0 10px;display:flex;flex-wrap:wrap;gap:18px 26px;align-items:flex-start}
.lin-strip-item{display:grid;gap:6px;width:132px;position:relative}
.lin-strip-item+.lin-strip-item::before,.lin-strip-gap+.lin-strip-item::before,.lin-strip-item+.lin-strip-gap::before{content:'→';position:absolute;left:-21px;top:104px;color:var(--ink-4,#55555a);font-size:.9rem}
.lin-strip-role{font-size:.68rem;letter-spacing:.08em;text-transform:uppercase;color:var(--ink-3,#85858a)}
.lin-strip-role em{font-style:normal;text-transform:none;letter-spacing:0}
.lin-strip-current .lin-strip-role{color:var(--gold,#d9c267)}
.lin-strip-gap{display:grid;align-content:start;padding-top:96px;font-size:.8rem;color:var(--ink-3,#85858a);position:relative}
.lin-strip-gap a{color:var(--gold,#d9c267)}
.lin-block-siblings{font-size:.86rem;line-height:1.7;color:var(--ink-2,#b4b4b0);margin:0}
.lin-block-siblings a{color:var(--ink,#f4f4f2)}
.lin-block .lin-tree-wrap{padding-left:0}
.lin-lab-card{display:flex;gap:16px;align-items:center;max-width:520px;padding:10px;border:1px solid var(--line,rgba(255,255,255,.12));color:var(--ink,#f4f4f2);text-decoration:none}
.lin-lab-card:hover,.lin-lab-card:focus-visible{color:var(--ink,#f4f4f2);text-decoration:none;border-color:var(--gold,#d9c267)}
.lin-lab-card:focus-visible{outline:2px solid var(--gold,#d9c267);outline-offset:3px}
.lin-lab-card img{width:120px;height:120px;object-fit:cover;flex:none;background:var(--bg1)}
.lin-lab-text{display:grid;gap:4px;min-width:0}
.lin-lab-title{font-size:1rem;line-height:1.4;overflow-wrap:anywhere}
.lin-lab-date{font-size:.78rem;color:var(--ink-3,#85858a)}
.lin-lab-go{font-size:.86rem;color:var(--gold,#d9c267)}
.lin-lab-private{display:grid;gap:4px;margin:0;color:var(--ink-2,#b4b4b0)}
</style>
